import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    MessageCircle,
    Download,
    RotateCw,
    Loader2,
    Tag as TagIcon,
    Pencil,
    Check,
    X,
} from "lucide-react";
import { adminApi, API_BASE } from "@/lib/api";
import { formatDateTime, renderMarkdown } from "@/lib/format";
import { toast } from "sonner";

const intentOptions = [
    { value: "", label: "All intents" },
    { value: "booking_lead", label: "Booking" },
    { value: "wedding_lead", label: "Wedding" },
    { value: "membership_lead", label: "Membership" },
    { value: "support_ticket", label: "Support" },
];

function timeAgo(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const sec = Math.floor((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return "just now";
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} min ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day} day${day === 1 ? "" : "s"} ago`;
    return formatDateTime(iso);
}

function previewText(c) {
    return c.preview ? c.preview.replace(/\[(.+?)\]/g, "$1") : "—";
}

async function downloadBlob(url, filename) {
    const token = localStorage.getItem("orchid_admin_token");
    const res = await fetch(`${API_BASE}${url}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(link.href);
}

export default function Conversations() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [filter, setFilter] = useState({ hasLead: "", tag: "" });
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [downloadingAll, setDownloadingAll] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const messagesEndRef = useRef(null);

    // "Improve answer" — review a bot reply and save the corrected version as a Q&A
    const [editingIdx, setEditingIdx] = useState(null);
    const [qaQuestion, setQaQuestion] = useState("");
    const [qaAnswer, setQaAnswer] = useState("");
    const [savingQA, setSavingQA] = useState(false);

    const precedingUserText = (idx) => {
        const msgs = detail?.messages || [];
        for (let j = idx - 1; j >= 0; j--) {
            if (msgs[j].role === "user") return (msgs[j].text || "").replace(/^\[|\]$/g, "");
        }
        return "";
    };

    const startImprove = (idx, botText) => {
        setEditingIdx(idx);
        setQaQuestion(precedingUserText(idx));
        setQaAnswer(botText || "");
    };

    const cancelImprove = () => {
        setEditingIdx(null);
        setQaQuestion("");
        setQaAnswer("");
    };

    const saveImprove = async () => {
        if (!qaQuestion.trim() || !qaAnswer.trim()) {
            toast.error("Both the question and the corrected answer are required.");
            return;
        }
        setSavingQA(true);
        try {
            await adminApi.post("/api/admin/qa", {
                question: qaQuestion.trim(),
                answer: qaAnswer.trim(),
                category: "Reviewed answer",
            });
            toast.success("Answer updated — Priya will use this next time.");
            cancelImprove();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save the correction.");
        } finally {
            setSavingQA(false);
        }
    };

    const load = async () => {
        setRefreshing(true);
        try {
            const params = {};
            if (filter.hasLead) params.has_lead = filter.hasLead === "yes";
            if (filter.tag) params.tag = filter.tag;
            const { data } = await adminApi.get("/api/admin/conversations", { params });
            setItems(data.items);
            setTotal(data.total);
            // Auto-select first if nothing chosen
            if (data.items.length && !selectedId) {
                setSelectedId(data.items[0].id);
            } else if (selectedId && !data.items.find((c) => c.id === selectedId)) {
                setSelectedId(data.items[0]?.id || null);
            }
        } finally {
            setRefreshing(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    useEffect(() => {
        if (!selectedId) {
            setDetail(null);
            return;
        }
        setDetailLoading(true);
        adminApi
            .get(`/api/admin/conversations/${selectedId}`)
            .then((r) => setDetail(r.data))
            .finally(() => setDetailLoading(false));
    }, [selectedId]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollTop = 0;
        }
        setEditingIdx(null);
    }, [detail?.id]);

    const visibleCount = items.length;

    const downloadOne = async () => {
        if (!detail) return;
        setDownloading(true);
        try {
            const start = (detail.started_at || "").slice(0, 10);
            const end = (detail.last_message_at || detail.started_at || "").slice(0, 10);
            await downloadBlob(
                `/api/admin/conversations/${detail.id}/export.pdf`,
                `chats_${detail.id.slice(0, 12)}_${start}~${end}.pdf`
            );
        } catch (err) {
            alert("Download failed: " + err.message);
        } finally {
            setDownloading(false);
        }
    };

    const downloadAll = async () => {
        setDownloadingAll(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            await downloadBlob(
                `/api/admin/exports/conversations.pdf`,
                `chats_all-conversations_${today}.pdf`
            );
        } catch (err) {
            alert("Export failed: " + err.message);
        } finally {
            setDownloadingAll(false);
        }
    };

    return (
        <div className="h-screen flex flex-col" data-testid="page-conversations">
            {/* Header bar */}
            <div className="px-8 pt-7 pb-4 border-b border-stone-200 bg-white flex items-end justify-between flex-shrink-0">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-1">
                        Conversations
                    </div>
                    <h1 className="font-heading text-4xl text-orchid-700">Chat logs</h1>
                    <p className="text-stone-600 text-sm mt-1.5">
                        {total} total · Every chat captured by the Orchid Concierge.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        disabled={refreshing}
                        data-testid="refresh-list"
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50 disabled:opacity-50"
                    >
                        <RotateCw size={13} className={refreshing ? "animate-spin" : ""} />
                        Refresh
                    </button>
                    <button
                        onClick={downloadAll}
                        disabled={downloadingAll || total === 0}
                        data-testid="download-all"
                        className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-xs font-medium px-4 py-2 rounded-md disabled:opacity-50"
                    >
                        {downloadingAll ? (
                            <Loader2 size={13} className="animate-spin" />
                        ) : (
                            <Download size={13} />
                        )}
                        {downloadingAll ? "Preparing…" : "Download all (PDF)"}
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="px-8 py-3 border-b border-stone-200 bg-white flex gap-2 items-center flex-shrink-0">
                <select
                    data-testid="filter-haslead"
                    value={filter.hasLead}
                    onChange={(e) => setFilter({ ...filter, hasLead: e.target.value })}
                    className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-xs text-stone-900"
                >
                    <option value="">All conversations</option>
                    <option value="yes">With lead/ticket</option>
                    <option value="no">Browsing only</option>
                </select>
                <select
                    data-testid="filter-tag"
                    value={filter.tag}
                    onChange={(e) => setFilter({ ...filter, tag: e.target.value })}
                    className="px-3 py-1.5 rounded-md border border-stone-200 bg-white text-xs text-stone-900"
                >
                    {intentOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
                <div className="text-[11px] text-stone-500 ml-auto">
                    Showing {visibleCount} of {total}
                </div>
            </div>

            {/* Split view */}
            <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[400px_1fr] overflow-hidden">
                {/* LEFT — list */}
                <aside
                    className="border-r border-stone-200 bg-white overflow-y-auto scroll-soft"
                    data-testid="chat-list"
                >
                    {items.length === 0 && (
                        <div className="text-center text-stone-500 py-16 text-sm">
                            No conversations yet.
                        </div>
                    )}
                    {items.map((c, idx) => {
                        const isActive = c.id === selectedId;
                        return (
                            <button
                                key={c.id}
                                onClick={() => setSelectedId(c.id)}
                                data-testid={`conversation-item-${idx}`}
                                data-conv-id={c.id}
                                className={`w-full text-left px-5 py-3.5 border-b border-stone-100 transition-colors ${
                                    isActive
                                        ? "bg-orchid-700/5 border-l-2 border-l-orchid-700"
                                        : "hover:bg-stone-50 border-l-2 border-l-transparent"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <div className="text-[13px] font-medium text-stone-900 truncate flex-1">
                                        {previewText(c)}
                                    </div>
                                    <div className="text-[10px] text-stone-500 whitespace-nowrap pt-0.5">
                                        {timeAgo(c.last_message_at || c.started_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {(c.intent_tags || []).slice(0, 2).map((t) => (
                                        <span
                                            key={t}
                                            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-orchid-700/10 text-orchid-700"
                                        >
                                            {t.replace("_", " ")}
                                        </span>
                                    ))}
                                    {c.has_lead && (
                                        <span className="text-[9px] uppercase tracking-wider text-orchid-500 font-medium">
                                            ✓ Lead
                                        </span>
                                    )}
                                    <span className="text-[10px] text-stone-400 ml-auto">
                                        {c.message_count} msg
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </aside>

                {/* RIGHT — detail */}
                <section className="flex flex-col min-h-0 bg-paper" data-testid="chat-detail">
                    {!selectedId ? (
                        <div className="flex-1 flex items-center justify-center text-stone-400 text-sm">
                            Select a conversation on the left to view the transcript.
                        </div>
                    ) : detailLoading || !detail ? (
                        <div className="flex-1 flex items-center justify-center text-stone-500 text-sm gap-2">
                            <Loader2 className="animate-spin" size={14} /> Loading…
                        </div>
                    ) : (
                        <>
                            {/* Detail header */}
                            <div className="px-6 py-4 border-b border-stone-200 bg-white flex items-center justify-between flex-shrink-0">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
                                        Session {detail.id.slice(0, 12)}
                                    </div>
                                    <div className="text-sm font-medium text-stone-900 mt-0.5 flex items-center gap-3 flex-wrap">
                                        <span>{formatDateTime(detail.started_at)}</span>
                                        <span className="text-stone-400">·</span>
                                        <span className="flex items-center gap-1 text-stone-600">
                                            <MessageCircle size={12} />
                                            {detail.messages?.length || 0} messages
                                        </span>
                                        {(detail.intent_tags || []).map((t) => (
                                            <span
                                                key={t}
                                                className="flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-orchid-700/10 text-orchid-700"
                                            >
                                                <TagIcon size={9} />
                                                {t.replace("_", " ")}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    onClick={downloadOne}
                                    disabled={downloading}
                                    data-testid="download-one"
                                    className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-orchid-700 text-orchid-700 hover:bg-orchid-700 hover:text-white transition-colors disabled:opacity-50"
                                >
                                    {downloading ? (
                                        <Loader2 size={13} className="animate-spin" />
                                    ) : (
                                        <Download size={13} />
                                    )}
                                    PDF
                                </button>
                            </div>

                            {/* Messages */}
                            <div
                                ref={messagesEndRef}
                                className="flex-1 overflow-y-auto scroll-soft px-6 py-5 space-y-3"
                                data-testid="chat-messages"
                            >
                                {(detail.messages || []).map((m, i) => {
                                    const isUser = m.role === "user";
                                    const isEditing = editingIdx === i;
                                    return (
                                        <div
                                            key={i}
                                            className={`flex flex-col ${
                                                isUser ? "items-end" : "items-start"
                                            }`}
                                        >
                                            <div
                                                className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                                                    isUser
                                                        ? "bg-orchid-700 text-white rounded-br-sm"
                                                        : "bg-white border border-stone-200 text-stone-800 rounded-bl-sm"
                                                }`}
                                            >
                                                <div
                                                    className="bot-text"
                                                    dangerouslySetInnerHTML={{
                                                        __html: renderMarkdown(m.text),
                                                    }}
                                                />
                                                {m.buttons && (
                                                    <div className="flex flex-wrap gap-1.5 mt-2 opacity-70">
                                                        {m.buttons.map((b, j) => (
                                                            <span
                                                                key={j}
                                                                className="text-[10px] px-2 py-0.5 rounded-full border border-current"
                                                            >
                                                                {b.label}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="text-[10px] opacity-60 mt-1.5">
                                                    {formatDateTime(m.timestamp)}
                                                </div>
                                            </div>

                                            {!isUser && (m.text || "").trim() && !isEditing && (
                                                <button
                                                    onClick={() => startImprove(i, m.text)}
                                                    data-testid={`improve-answer-btn-${i}`}
                                                    className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-orchid-700 hover:text-orchid-600 transition-colors"
                                                >
                                                    <Pencil size={11} /> Improve answer
                                                </button>
                                            )}

                                            {!isUser && isEditing && (
                                                <div
                                                    className="mt-2 w-full max-w-[92%] bg-white border border-orchid-700/40 rounded-xl p-4 shadow-card"
                                                    data-testid="improve-editor"
                                                >
                                                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">
                                                        Question the guest asked
                                                    </div>
                                                    <input
                                                        value={qaQuestion}
                                                        onChange={(e) => setQaQuestion(e.target.value)}
                                                        data-testid="improve-question-input"
                                                        placeholder="e.g. What are the Gold membership benefits?"
                                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                                                    />
                                                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mt-3 mb-1">
                                                        Corrected answer Priya should give
                                                    </div>
                                                    <textarea
                                                        rows={5}
                                                        value={qaAnswer}
                                                        onChange={(e) => setQaAnswer(e.target.value)}
                                                        data-testid="improve-answer-input"
                                                        className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700 focus:ring-2 focus:ring-orchid-700/15"
                                                    />
                                                    <div className="flex items-center justify-between gap-2 mt-3">
                                                        <p className="text-[10px] text-stone-500">
                                                            Saved as a Q&amp;A pair — Priya answers this
                                                            correctly next time.
                                                        </p>
                                                        <div className="flex gap-2 shrink-0">
                                                            <button
                                                                onClick={cancelImprove}
                                                                data-testid="improve-cancel-btn"
                                                                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50"
                                                            >
                                                                <X size={12} /> Cancel
                                                            </button>
                                                            <button
                                                                onClick={saveImprove}
                                                                disabled={savingQA}
                                                                data-testid="improve-save-btn"
                                                                className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white font-medium disabled:opacity-50"
                                                            >
                                                                {savingQA ? (
                                                                    <Loader2 size={12} className="animate-spin" />
                                                                ) : (
                                                                    <Check size={12} />
                                                                )}
                                                                Update Answer
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {(!detail.messages || detail.messages.length === 0) && (
                                    <div className="text-center text-stone-400 text-sm py-8">
                                        No messages yet.
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </section>
            </div>
        </div>
    );
}
